import { call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import ACTION_CONSTANTS from '../constants/actionConstants';
import RecordBinderService from '../services/api/recordBinder';
import {
  getRecordBindersSuccess,
  getRecordBindersFailure,
  getBinRecordBindersSuccess,
  getBinRecordBindersFailure,
  getRecordHistoryRequest,
  getRecordHistorySuccess,
  getRecordHistoryFailure,
  getRecordHistoryDetailsSuccess,
  getRecordHistoryDetailsFailure,
  addRecordSuccess,
  addRecordFailure,
  updateRecordSuccess,
  updateRecordFailure,
  deleteRecordSuccess,
  deleteRecordFailure,
  uploadRecordAttachmentSuccess,
  uploadRecordAttachmentFailure,
} from '../store/actions/recordBinder';
import localization from '../localization/i18n';
import { getLocale } from '../services/storageService/GlobalData';
import RecordBinderManager from '../database/reactDataManager/RecordBinderManager';
import MetaDataManager from '../database/reactDataManager/MetaDataManager';
import metaDataDto from '../models/metaDataModel';
import { MODULE_NAMES, RECORD_BINDER_STATUS } from '../constants/AppConstants';

export const recordBinderStoreRequest = (state) => state.recordBinder;

export function* getRecordBindersAsync({ payload }) {
  try {
    let recordBinderStore = yield select(recordBinderStoreRequest);
    let lastSynced = null;
    let recordBinderMetaData = yield call(
      MetaDataManager.getMetaDataByModule,
      MODULE_NAMES.RECORD_BINDER_MODULE,
    );
    if (recordBinderMetaData) {
      lastSynced = recordBinderMetaData.lastSynced;
    }

    const update = payload.update;

    const query = recordBinderStore.query;
    const filter = recordBinderStore.filter;

    if (navigator.onLine && update) {
      try {
        const response = yield call(
          RecordBinderService.getRecordBinders,
          payload?.feedlotId,
          lastSynced,
        );

        if (response) {
          if (response?.time) {
            let metaDataModel = metaDataDto({
              moduleName: MODULE_NAMES.RECORD_BINDER_MODULE,
              lastSynced: response.time,
            });
            yield call(MetaDataManager.saveMetaData, metaDataModel);
            lastSynced = response.time;
          }

          const feedlotRecords = response.data.feedlotRecords || [];
          const transformedData = feedlotRecords.map((record) => ({
            id: record.id,
            record_binder_id: record.recordBinder.record_binder_id,
            record_name: record.recordBinder.name,
            feedlot_id: record.feedlotId,
            sops: record.recordBinder.sops || [],
            sop_count: record.recordBinder.sops
              ? record.recordBinder.sops.length
              : 0,
            expiry_date: record.expiryDate || null,
            status: record.status,
            admin_status: record.recordBinder.status,
            instructions: record.recordBinder.instructions,
            created_at: record.createDatetime,
            updated_at: record.updateDatetime,
            expiry_date_color: record.expiryDateColor,
          }));

          if (transformedData.length > 0) {
            yield call(RecordBinderManager.saveRecordBinders, transformedData);
          }
        }
      } catch (e) {
        console.log(
          'error in api call for record binders, returning data from db',
          e,
        );
      }
    }

    let recordBinders = yield call(
      RecordBinderManager.getRecordBinders,
      query,
      filter,
    );

    yield put(getRecordBindersSuccess(recordBinders));
  } catch (error) {
    yield put(
      getRecordBindersFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

function* getRecordBinders({ payload }) {
  yield call(getRecordBindersAsync, { payload });
}

export function* getBinRecordBindersAsync({ payload }) {
  try {
    const recordBinderStore = yield select(recordBinderStoreRequest);
    const query = recordBinderStore.binQuery;
    const binFilter = {
      status: RECORD_BINDER_STATUS.BIN,
      startDate: null,
      endDate: null,
      sort: payload?.sort || false,
    };

    const recordBinders = yield call(
      RecordBinderManager.getRecordBinders,
      query,
      binFilter,
    );

    yield put(getBinRecordBindersSuccess(recordBinders));
  } catch (error) {
    yield put(
      getBinRecordBindersFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

function* getBinRecordBinders({ payload }) {
  yield call(getBinRecordBindersAsync, { payload });
}

function* getRecordHistory({ payload }) {
  try {
    const recordId = payload?.recordId;
    const update = payload?.update !== false;
    let recordBinderStore = yield select(recordBinderStoreRequest);
    const historyFilter =
      payload?.filter || recordBinderStore.historyFilter || {};

    if (navigator.onLine && update) {
      try {
        const response = yield call(
          RecordBinderService.getRecordHistory,
          recordId,
        );
        if (response && response.data) {
          const histories = response?.data?.feedlotRecordHistories || [];
          yield call(
            RecordBinderManager.saveRecordHistory,
            recordId,
            histories,
          );
        }
      } catch (e) {
        console.log('error in api call for record history', e);
      }
    }

    const histories = yield call(
      RecordBinderManager.getRecordHistory,
      recordId,
      historyFilter,
    );

    yield put(getRecordHistorySuccess(histories || []));
  } catch (error) {
    yield put(
      getRecordHistoryFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

function* getRecordHistoryDetails({ payload }) {
  try {
    const id = payload?.id;
    const response = yield call(
      RecordBinderService.getRecordHistoryDetails,
      id,
    );
    if (response && response.data) {
      yield put(getRecordHistoryDetailsSuccess(response.data));
    } else {
      yield put(
        getRecordHistoryDetailsFailure(
          localization[getLocale()].SOMETHING_WENT_WRONG ||
            'Something went wrong',
        ),
      );
    }
  } catch (error) {
    yield put(
      getRecordHistoryDetailsFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

// AddRecord reads the result back via state.recordBinder.attachmentUploads[tempId]
function* uploadRecordAttachment({ payload }) {
  const { tempId, ...uploadPayload } = payload;
  try {
    const response = yield call(
      RecordBinderService.uploadAttachment,
      uploadPayload,
    );

    const attachmentId = response?.data?.attachment?.attachmentId;
    if (attachmentId) {
      yield put(
        uploadRecordAttachmentSuccess({
          tempId,
          attachmentId,
        }),
      );
    } else {
      yield put(
        uploadRecordAttachmentFailure({
          tempId,
          error: localization[getLocale()].SOMETHING_WENT_WRONG,
        }),
      );
    }
  } catch (error) {
    yield put(
      uploadRecordAttachmentFailure({
        tempId,
        error: error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      }),
    );
  }
}

function* addRecord({ payload }) {
  try {
    const response = yield call(RecordBinderService.addRecord, payload);
    if (response && response.data) {
      yield put(addRecordSuccess(response.data));
    } else {
      yield put(
        addRecordFailure(
          localization[getLocale()].SOMETHING_WENT_WRONG ||
            'Something went wrong',
        ),
      );
    }
  } catch (error) {
    yield put(
      addRecordFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

function* updateRecord({ payload }) {
  try {
    const { id, ...recordData } = payload;
    const response = yield call(
      RecordBinderService.updateRecord,
      id,
      recordData,
    );
    if (response && response.data) {
      yield put(updateRecordSuccess(response.data));
    } else {
      yield put(
        updateRecordFailure(
          localization[getLocale()].SOMETHING_WENT_WRONG ||
            'Something went wrong',
        ),
      );
    }
  } catch (error) {
    yield put(
      updateRecordFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

function* deleteRecord({ payload }) {
  try {
    const { id, recordBinder } = payload;
    const response = yield call(RecordBinderService.deleteRecord, id);
    if (response && response.data) {
      yield put(deleteRecordSuccess(response.data));
      if (recordBinder?.id) {
        yield put(getRecordHistoryRequest({ recordId: recordBinder.id }));
      }
    } else {
      yield put(
        deleteRecordFailure(
          localization[getLocale()].SOMETHING_WENT_WRONG ||
            'Something went wrong',
        ),
      );
    }
  } catch (error) {
    yield put(
      deleteRecordFailure(
        error.message || localization[getLocale()].SOMETHING_WENT_WRONG,
      ),
    );
  }
}

export default function* recordBinderSaga() {
  yield takeLatest(
    ACTION_CONSTANTS.GET_RECORD_BINDERS_REQUESTED,
    getRecordBinders,
  );
  yield takeLatest(
    ACTION_CONSTANTS.GET_BIN_RECORD_BINDERS_REQUESTED,
    getBinRecordBinders,
  );
  yield takeLatest(
    ACTION_CONSTANTS.GET_RECORD_HISTORY_REQUESTED,
    getRecordHistory,
  );
  yield takeLatest(
    ACTION_CONSTANTS.GET_RECORD_HISTORY_DETAILS_REQUESTED,
    getRecordHistoryDetails,
  );
  yield takeLatest(ACTION_CONSTANTS.ADD_RECORD_REQUESTED, addRecord);
  yield takeLatest(ACTION_CONSTANTS.UPDATE_RECORD_REQUESTED, updateRecord);
  yield takeLatest(ACTION_CONSTANTS.DELETE_RECORD_REQUESTED, deleteRecord);
  yield takeEvery(
    ACTION_CONSTANTS.UPLOAD_RECORD_ATTACHMENT_REQUESTED,
    uploadRecordAttachment,
  );
}
